import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Bug, AlertTriangle, Send, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';

interface BugReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const BugReportDialog: React.FC<BugReportDialogProps> = ({ open, onOpenChange }) => {
  const location = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [summary, setSummary] = useState('');
  const [description, setDescription] = useState('');
  const [includeTechnicalDetails, setIncludeTechnicalDetails] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!summary.trim() || !description.trim()) {
      toast({
        title: "Missing information",
        description: "Please provide both a summary and description.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Build technical details if enabled
      let technicalInfo = '';
      if (includeTechnicalDetails) {
        technicalInfo = `

---
Technical Details:
- Route: ${location.pathname}${location.search}
- Timestamp: ${new Date().toISOString()}
- Screen: ${window.innerWidth}x${window.innerHeight}
- User Agent: ${navigator.userAgent}
`;
      }

      const payload = {
        name: user?.email?.split('@')[0] || 'Anonymous User',
        email: user?.email || 'noreply@lexsavvy.ai',
        subject: `Bug Report: ${summary}`,
        category: 'technical',
        message: `${description}${technicalInfo}`,
      };

      const { error } = await supabase.functions.invoke('send-contact-form', {
        body: payload,
      });

      if (error) throw error;

      toast({
        title: "Bug report submitted",
        description: "Thank you for helping us improve LexSavvy!",
      });

      // Reset form and close
      setSummary('');
      setDescription('');
      onOpenChange(false);
    } catch (error) {
      console.error('Error submitting bug report:', error);
      toast({
        title: "Failed to submit",
        description: "Please try again or contact support directly.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bug className="h-5 w-5 text-primary" />
            Report a Bug
          </DialogTitle>
          <DialogDescription>
            Help us improve by reporting issues you encounter.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Alert className="border-border bg-muted">
            <AlertTriangle className="h-4 w-4 text-primary" />
            <AlertDescription className="text-sm text-primary">
              Do not include confidential client information, privileged communications, or sensitive financial data.
            </AlertDescription>
          </Alert>

          <div className="space-y-2">
            <Label htmlFor="summary">Summary</Label>
            <Input
              id="summary"
              placeholder="Brief description of the issue"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              maxLength={100}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">What happened?</Label>
            <Textarea
              id="description"
              placeholder="Describe what you were doing and what went wrong. Include steps to reproduce if possible."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={5}
              maxLength={2000}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Switch
                id="include-technical"
                checked={includeTechnicalDetails}
                onCheckedChange={setIncludeTechnicalDetails}
              />
              <Label htmlFor="include-technical" className="text-sm text-muted-foreground cursor-pointer">
                Include technical details (page, screen size)
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  Submit Report
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default BugReportDialog;
